import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Link2, Users, Search, FileCheck, Phone } from "lucide-react";

const ModernSlavery = () => {
  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Modern Slavery Statement
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Modern Slavery Statement</h1>
              <p className="text-slate-500">Financial Year 2024-2025</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600 mb-8">
              This statement is made pursuant to Section 54 of the Modern Slavery Act 2015. It sets out the steps KiddiPlay Club has taken to ensure that modern slavery and human trafficking are not taking place in our business or supply chains.
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Our Business</h2>
              <p className="text-slate-600">
                KiddiPlay Club is a UK-based technology company that operates a social platform connecting families for playdates and shared childcare. We are committed to acting ethically and with integrity in all our business relationships.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Search className="w-5 h-5 text-slate-600" />
                Our Supply Chain
              </h2>
              <p className="text-slate-600 mb-4">As a digital platform, our supply chain primarily consists of:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Cloud hosting and infrastructure providers</li>
                <li>Payment processing services</li>
                <li>Software and development tools</li>
                <li>Professional services (legal, accounting)</li>
                <li>Marketing and advertising services</li>
              </ul>
              <p className="text-slate-600 mt-4">
                We work with established, reputable suppliers who are subject to their own modern slavery compliance requirements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <FileCheck className="w-5 h-5 text-slate-600" />
                Our Policies
              </h2>
              <p className="text-slate-600 mb-4">We have implemented the following policies to prevent modern slavery:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Anti-Slavery Policy:</strong> We prohibit the use of forced, compulsory, or trafficked labour in any part of our business</li>
                <li><strong>Supplier Code of Conduct:</strong> We expect our suppliers to share our commitment to ethical practices</li>
                <li><strong>Whistleblowing Policy:</strong> We encourage employees to report concerns without fear of retaliation</li>
                <li><strong>Recruitment Policy:</strong> We verify the right to work of all employees and pay at least the National Living Wage</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Users className="w-5 h-5 text-slate-600" />
                Our Platform
              </h2>
              <p className="text-slate-600 mb-4">
                We are vigilant about the potential misuse of our platform. Our safeguarding measures include:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Monitoring for suspicious patterns of behaviour</li>
                <li>Clear reporting mechanisms for users</li>
                <li>Cooperation with law enforcement when required</li>
                <li>User verification and identity checks</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Due Diligence</h2>
              <p className="text-slate-600">
                We conduct due diligence on new suppliers and regularly review existing supplier relationships. We assess the risk of modern slavery in our supply chain and take steps to mitigate identified risks.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Training</h2>
              <p className="text-slate-600">
                Our team receives training on modern slavery awareness, including how to identify potential signs of exploitation and how to report concerns appropriately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Effectiveness</h2>
              <p className="text-slate-600">
                We measure the effectiveness of our efforts through:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Regular review of our policies and procedures</li>
                <li>Monitoring of supplier compliance</li>
                <li>Tracking of reports and concerns raised</li>
                <li>Annual review of this statement</li>
              </ul>
            </section>

            <section className="p-6 bg-slate-100 rounded-xl">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Phone className="w-5 h-5 text-slate-600" />
                Report a Concern
              </h2>
              <p className="text-slate-600 mb-4">
                If you suspect modern slavery in any part of our business or supply chain, please contact us:
              </p>
              <p className="text-slate-700">
                <strong>Email:</strong> <a href="mailto:compliance@kiddiplayclub.com" className="text-purple-600 underline">compliance@kiddiplayclub.com</a>
              </p>
              <p className="text-slate-600 mt-4">
                You can also contact the Modern Slavery Helpline: <strong>08000 121 700</strong>
              </p>
            </section>

            <section className="mt-8 pt-8 border-t border-slate-200">
              <p className="text-slate-500 text-sm">
                This statement has been approved by the leadership of KiddiPlay Club and will be reviewed and updated annually.
              </p>
              <p className="text-slate-500 text-sm mt-2">
                <strong>Date:</strong> January 2025
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ModernSlavery;
